import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Plus, Archive, Loader2, Clock, User, ArchiveRestore } from 'lucide-react';

const AUTHOR_STYLES = {
  adam:   'bg-[#7B61FF]/20 text-[#7B61FF]',
  vito:   'bg-emerald-500/20 text-emerald-400',
  system: 'bg-[#F0EFF4]/10 text-[#F0EFF4]/50',
  devin:  'bg-blue-500/20 text-blue-400',
};

const AUTHORS = ['adam', 'vito', 'system'];

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

const ClientNotes = ({ clientId, password, getAuthHeaders }) => {
  const authHeaders = (extra = {}) => {
    const base = getAuthHeaders
      ? getAuthHeaders()
      : { 'Content-Type': 'application/json', 'x-admin-password': password };
    return { ...base, ...extra };
  };
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [body, setBody] = useState('');
  const [author, setAuthor] = useState('adam');
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(null);

  const fetchNotes = useCallback(async () => {
    try {
      // GET: send auth as query param (legacy compat) + headers for JWT path
      const params = new URLSearchParams({ client_id: clientId });
      if (password) params.set('password', password);
      if (showArchived) params.set('include_archived', 'true');
      const headers = getAuthHeaders ? getAuthHeaders() : (password ? { 'x-admin-password': password } : {});
      const res = await fetch(`/api/client-notes?${params}`, { headers });
      if (!res.ok) return;
      const data = await res.json();
      setNotes(data.notes || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [clientId, password, getAuthHeaders, showArchived]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleAdd = async () => {
    if (!body.trim()) return;
    setSaving(true);
    try {
      const res = await fetch('/api/client-notes', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ client_id: clientId, author, body: body.trim() }),
      });
      if (!res.ok) throw new Error('Failed to add note');
      setBody('');
      setShowAdd(false);
      await fetchNotes();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const handleArchive = async (noteId, isArchived) => {
    setArchiving(noteId);
    try {
      const res = await fetch('/api/client-notes', {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ id: noteId, archived: !isArchived, archived_by: 'admin' }),
      });
      if (!res.ok) throw new Error('Failed to archive');
      await fetchNotes();
    } catch (err) { alert(err.message); }
    finally { setArchiving(null); }
  };

  return (
    <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[#7B61FF]" />
          <h3 className="text-sm font-bold text-[#F0EFF4] uppercase tracking-wider font-data">Notes & Activity</h3>
          <span className="text-xs text-[#F0EFF4]/30 font-data ml-1">
            {notes.filter(n => !n.archived).length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowArchived(v => !v)}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
              showArchived ? 'bg-[#F0EFF4]/10 text-[#F0EFF4]/70' : 'text-[#F0EFF4]/30 hover:text-[#F0EFF4]/50'
            }`}
          >
            <Archive className="w-3 h-3" />
            {showArchived ? 'Hide archived' : 'Show archived'}
          </button>
          <button
            onClick={() => setShowAdd(v => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#7B61FF]/10 hover:bg-[#7B61FF]/20 border border-[#7B61FF]/20 rounded-lg text-xs text-[#7B61FF] font-semibold transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Add Note
          </button>
        </div>
      </div>

      {/* Add note form */}
      {showAdd && (
        <div className="mb-4 p-4 bg-[#0A0A14]/60 rounded-xl border border-[#7B61FF]/20 space-y-3">
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Write a note..."
            className="w-full bg-[#0A0A14] border border-[#F0EFF4]/10 rounded-lg px-3 py-2 text-sm text-[#F0EFF4] placeholder:text-[#F0EFF4]/20 focus:border-[#7B61FF] focus:outline-none resize-y min-h-[60px]"
            autoFocus
            onKeyDown={e => { if (e.key === 'Enter' && e.metaKey) handleAdd(); }}
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#F0EFF4]/40">Author:</span>
              {AUTHORS.map(a => (
                <button
                  key={a}
                  onClick={() => setAuthor(a)}
                  className={`px-2.5 py-1 rounded-full text-xs font-data font-semibold transition-colors ${
                    author === a ? AUTHOR_STYLES[a] : 'bg-[#F0EFF4]/5 text-[#F0EFF4]/40 hover:text-[#F0EFF4]'
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-xs text-[#F0EFF4]/40 hover:text-[#F0EFF4]">Cancel</button>
              <button
                onClick={handleAdd}
                disabled={saving || !body.trim()}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-[#7B61FF] hover:bg-[#6B51EF] text-white text-xs font-bold rounded-lg transition-colors disabled:opacity-40"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                Save Note
              </button>
            </div>
          </div>
          <p className="text-xs text-[#F0EFF4]/20">⌘+Enter to save</p>
        </div>
      )}

      {/* Notes list */}
      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-[#7B61FF]" />
        </div>
      ) : notes.length === 0 ? (
        <p className="text-sm text-[#F0EFF4]/30 italic text-center py-4">
          No notes yet. Add one to start tracking activity.
        </p>
      ) : (
        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
          {notes.map(note => (
            <div
              key={note.id}
              className={`flex gap-3 p-3 rounded-xl border transition-all ${
                note.archived
                  ? 'bg-[#0A0A14]/30 border-[#F0EFF4]/5 opacity-50'
                  : 'bg-[#0A0A14]/50 border-[#F0EFF4]/10'
              }`}
            >
              <div className="shrink-0 mt-0.5">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${AUTHOR_STYLES[note.author] || AUTHOR_STYLES.system}`}>
                  {(note.author || '?')[0].toUpperCase()}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-data font-semibold ${AUTHOR_STYLES[note.author] ? AUTHOR_STYLES[note.author].split(' ')[1] : 'text-[#F0EFF4]/50'}`}>
                    {note.author}
                  </span>
                  <span className="text-xs text-[#F0EFF4]/30 font-data flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {timeAgo(note.created_at)}
                  </span>
                  {note.archived && (
                    <span className="text-xs text-[#F0EFF4]/20 font-data italic">archived</span>
                  )}
                </div>
                <p className="text-sm text-[#F0EFF4]/80 whitespace-pre-wrap">{note.body}</p>
              </div>
              <button
                onClick={() => handleArchive(note.id, note.archived)}
                disabled={archiving === note.id}
                className="shrink-0 p-1.5 text-[#F0EFF4]/20 hover:text-[#F0EFF4]/50 transition-colors self-start"
                title={note.archived ? 'Unarchive' : 'Archive'}
              >
                {archiving === note.id ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : note.archived ? (
                  <ArchiveRestore className="w-3.5 h-3.5" />
                ) : (
                  <Archive className="w-3.5 h-3.5" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientNotes;
